import type { ITask } from '@modules/TasksModule/types';

/**
 * Task type of a manual booking card: a booking the CSR starts outside a call
 * or text, from "+ New booking" in Assigned to Me or from Book Job on the Inbox
 * home view. The cards are client-only and exist only with the new Assigned to
 * Me gate on.
 */
export const MANUAL_BOOKING_TASK_TYPE = 'manual-booking';

/**
 * Task attribute of a manual booking card started from a customer's Book Job:
 * its view opens Book Job at once, at this location. Its view clears it once
 * used, so a later remount opens the customer instead.
 */
export const OPEN_BOOK_JOB_ATTRIBUTE = 'openBookJob';

export type OpenBookJobAttribute = {
  /** The location selected where the CSR clicked Book Job; null for none. */
  locationId: string | null;
};

export const isManualBookingTask = (task: Pick<ITask, 'type'>): boolean =>
  task.type === MANUAL_BOOKING_TASK_TYPE;

/** A manual booking card's name until a customer is picked. */
export const NEW_BOOKING_TITLE = 'New booking';

/** The name a manual booking card shows: its customer's, else "New booking". */
export const manualBookingName = (
  task: Pick<ITask, 'attributes' | 'displayName'>,
): string =>
  (task.attributes.customerId && task.displayName) || NEW_BOOKING_TITLE;

/** A manual booking card's `OPEN_BOOK_JOB_ATTRIBUTE`, if it is set. */
export const getOpenBookJob = (
  task: Pick<ITask, 'attributes'> | undefined,
): OpenBookJobAttribute | undefined =>
  task?.attributes[OPEN_BOOK_JOB_ATTRIBUTE] as OpenBookJobAttribute | undefined;
